<template>
  <div>
    <v-progress-linear v-if="loading" indeterminate color="primary" />

    <!-- Mobile View -->
    <template v-if="device.isMobile">
      <v-toolbar flat dense class="mb-1">
        <v-spacer />
        <excel-button class="mr-2" @click="exportExcel" />
        <print-button class="mr-3" @click="print" />
        <v-btn-toggle v-model="aggregateMode" dense class="mr-2">
          <v-btn :value="true" small>
            <v-icon left small>mdi-sigma</v-icon>
            {{ $t("warehouse.aggregate") }}
          </v-btn>
        </v-btn-toggle>
      </v-toolbar>

      <template v-if="aggregateMode">
        <mobile-data-cards
          v-if="aggregatedItems.length > 0 || loading"
          :headers="aggregateMobileHeaders"
          :items="aggregatedItems"
          :loading="loading"
          item-key="grocery_id"
          :show-row-number="false"
        >
          <template v-slot:item.grocery_name="{ item }">
            {{ toLatin(item.grocery_name) }}
          </template>
          <template v-slot:item.total_amount="{ item }">
            {{ item.total_amount }} {{ item.grocery_unit }}
          </template>
        </mobile-data-cards>
        <empty-state
          v-if="!loading && aggregatedItems.length === 0"
          icon="mdi-sigma"
          :title="$t('warehouse.no_aggregate_data')"
        />
      </template>
      <template v-else>
        <mobile-data-cards
          v-if="documents.length > 0 || loading"
          :headers="mobileHeaders"
          :items="documents"
          :loading="loading"
          item-key="id"
          :show-row-number="false"
        >
          <template v-slot:item.status="{ item }">
            <v-chip small :color="getStatusColor(item.status)" dark>
              {{ $t(statusKeyMap[item.status] || item.status) }}
            </v-chip>
          </template>
          <template v-slot:item.document_date="{ item }">
            {{ localeDate(item.document_date) }}
          </template>
          <template v-slot:item.restaurant_name="{ item }">
            {{ toLatin(restaurants.find((r) => r.id === item.restaurant_id)?.name || "—") }}
          </template>
          <template v-slot:item.offer_deadline="{ item }">
            {{ localeDate(item.offer_deadline) }}
          </template>
          <template v-slot:item.delivery_deadline="{ item }">
            {{ localeDate(item.delivery_deadline) }}
          </template>
          <template v-slot:item.notes="{ item }">
            <span v-if="item.notes">{{ toLatin(item.notes) }}</span>
            <span v-else class="grey--text">-</span>
          </template>
          <template v-slot:item.actions="{ item }">
            <circle-button
              icon="mdi-eye"
              :tooltip="$t('overview')"
              color="primary"
              @click="$emit('open-detail', item)"
            />
          </template>
        </mobile-data-cards>
        <empty-state
          v-if="!loading && documents.length === 0"
          icon="mdi-file-question-outline"
          :title="$t('no_data')"
          :subtitle="$t('no_documents_for_supplier')"
        />
      </template>
    </template>

    <!-- Desktop View -->
    <template v-else>
      <!-- Aggregate view -->
      <template v-if="aggregateMode">
        <v-data-table
          v-if="aggregatedItems.length > 0 || loading"
          :headers="aggregateHeaders"
          :items="aggregatedItems"
          :loading="loading"
          item-key="grocery_id"
          show-expand
          single-expand
          hide-default-footer
          :items-per-page="-1"
          class="striped-table ordera-box-shadow"
        >
          <template v-slot:top>
            <v-toolbar flat dense>
              <v-spacer />
              <excel-button class="mr-2" @click="exportExcel" />
              <print-button class="mr-3" @click="print" />
              <v-btn-toggle v-model="aggregateMode" dense class="mr-2">
                <v-btn :value="true" small>
                  <v-icon left small>mdi-sigma</v-icon>
                  {{ $t("warehouse.aggregate") }}
                </v-btn>
              </v-btn-toggle>
            </v-toolbar>
          </template>
          <template v-slot:item.grocery_name="{ item }">
            {{ toLatin(item.grocery_name) }}
          </template>
          <template v-slot:item.total_amount="{ item }">
            {{ item.total_amount }} {{ item.grocery_unit }}
          </template>
          <template v-slot:expanded-item="{ item: grocery }">
            <td :colspan="aggregateHeaders.length + 1" class="pa-0">
              <v-data-table
                :headers="expandedSubHeaders"
                :items="grocery.source_items"
                hide-default-footer
                :items-per-page="-1"
                dense
                class="grey lighten-5 elevation-0"
              >
                <template v-slot:item.doc_date="{ item }">
                  {{ localeDate(item.doc_date) }}
                </template>
                <template v-slot:item.doc_status="{ item }">
                  <v-chip x-small :color="getStatusColor(item.doc_status)" dark>
                    {{ $t(statusKeyMap[item.doc_status] || item.doc_status) }}
                  </v-chip>
                </template>
                <template v-slot:item.restaurant_name="{ item }">
                  {{
                    toLatin(
                      restaurants.find((r) => r.id === item.restaurant_id)?.name || "—"
                    )
                  }}
                </template>
              </v-data-table>
            </td>
          </template>
        </v-data-table>
        <empty-state
          v-if="!loading && aggregatedItems.length === 0"
          icon="mdi-sigma"
          :title="$t('warehouse.no_aggregate_data')"
        />
      </template>

      <!-- Normal view -->
      <template v-else>
        <empty-state
          v-if="!loading && documents.length === 0"
          icon="mdi-file-question-outline"
          :title="$t('no_data')"
          :subtitle="$t('no_documents_for_supplier')"
        />
        <v-data-table
          v-else
          :headers="headers"
          :items="documents"
          :loading="loading"
          class="striped-table ordera-box-shadow"
          :items-per-page="15"
        >
          <template v-slot:top>
            <v-toolbar flat dense>
              <v-spacer />
              <excel-button class="mr-2" @click="exportExcel" />
              <print-button class="mr-3" @click="print" />
              <v-btn-toggle v-model="aggregateMode" dense class="mr-2">
                <v-btn :value="true" small>
                  <v-icon left small>mdi-sigma</v-icon>
                  {{ $t("warehouse.aggregate") }}
                </v-btn>
              </v-btn-toggle>
            </v-toolbar>
          </template>
          <template v-slot:item.status="{ item }">
            <v-chip small :color="getStatusColor(item.status)" dark>
              {{ $t(statusKeyMap[item.status] || item.status) }}
            </v-chip>
          </template>
          <template v-slot:item.document_date="{ item }">
            {{ localeDate(item.document_date) }}
          </template>
          <template v-slot:item.offer_deadline="{ item }">
            {{ localeDate(item.offer_deadline) }}
          </template>
          <template v-slot:item.restaurant_name="{ item }">
            {{ toLatin(restaurants.find((r) => r.id === item.restaurant_id)?.name || "—") }}
          </template>
          <template v-slot:item.delivery_deadline="{ item }">
            {{ localeDate(item.delivery_deadline) }}
          </template>
          <template v-slot:item.notes="{ item }">
            <span v-if="item.notes" class="text-truncate d-block" style="max-width: 200px">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on">{{ toLatin(item.notes) }}</span>
                </template>
                <span>{{ toLatin(item.notes) }}</span>
              </v-tooltip>
            </span>
            <span v-else class="grey--text">-</span>
          </template>
          <template v-slot:item.actions="{ item }">
            <circle-button
              icon="mdi-eye"
              :tooltip="$t('overview')"
              color="primary"
              @click="$emit('open-detail', item)"
            />
          </template>
        </v-data-table>
      </template>
    </template>
  </div>
</template>

<script>
import EmptyState from "@/components/global/EmptyState.vue";
import MobileDataCards from "@/components/global/MobileDataCards.vue";
import warehouseFiltersMixin from "@/mixins/warehouse/warehouseFiltersMixin";
import hasPrintingV2 from "@/mixins/hasPrintingV2";
import hasExcel from "@/mixins/hasExcel";

export default {
  name: "SupplierPortalRequestQuoteTab",
  mixins: [warehouseFiltersMixin, hasPrintingV2, hasExcel],
  components: { EmptyState, MobileDataCards },
  props: {
    documents: { type: Array, default: () => [] },
    restaurants: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
  },
  data() {
    return { aggregateMode: false };
  },
  computed: {
    aggregatedItems() {
      const map = {};
      for (const doc of this.documents) {
        for (const item of doc.items || []) {
          const key = item.grocery_id;
          if (!key) continue;
          if (!map[key]) {
            map[key] = {
              grocery_id: key,
              grocery_name: item.grocery?.name || `#${key}`,
              grocery_unit: item.grocery?.unit || "",
              total_amount: 0,
              source_items: [],
            };
          }
          map[key].total_amount += Number(item.amount) || 0;
          map[key].source_items.push({
            doc_id: doc.id,
            doc_number: doc.number || doc.id,
            doc_date: doc.document_date,
            doc_status: doc.status,
            restaurant_id: doc.restaurant_id,
            amount: item.amount,
          });
        }
      }
      return Object.values(map).sort((a, b) =>
        (a.grocery_name || "").localeCompare(b.grocery_name || "")
      );
    },
    aggregateHeaders() {
      return [
        { text: this.$t("title"), value: "grocery_name" },
        { text: this.$t("q"), value: "total_amount" },
        { text: "", value: "data-table-expand" },
      ];
    },
    expandedSubHeaders() {
      return [
        { text: this.$t("warehouse.document_number"), value: "doc_number" },
        { text: this.$t("date"), value: "doc_date" },
        { text: this.$t("status"), value: "doc_status" },
        { text: this.$t("restaurant"), value: "restaurant_name" },
        { text: this.$t("q"), value: "amount" },
      ];
    },
    headers() {
      return [
        { text: "ID", value: "id", width: "70px" },
        { text: this.$t("warehouse.document_number"), value: "number" },
        { text: this.$t("status"), value: "status" },
        { text: this.$t("date"), value: "document_date" },
        { text: this.$t("restaurant"), value: "restaurant_name" },
        { text: this.$t("warehouse.offers_deadline"), value: "offer_deadline" },
        { text: this.$t("warehouse.delivery_deadline"), value: "delivery_deadline" },
        { text: this.$t("note"), value: "notes" },
        {
          text: this.$t("actions"),
          value: "actions",
          sortable: false,
          align: "right",
          width: "80px",
        },
      ];
    },
    mobileHeaders() {
      return [
        { text: this.$t("warehouse.document_number"), value: "number" },
        { text: this.$t("status"), value: "status" },
        { text: this.$t("date"), value: "document_date" },
        { text: this.$t("restaurant"), value: "restaurant_name" },
        { text: this.$t("warehouse.offers_deadline"), value: "offer_deadline" },
        { text: this.$t("warehouse.delivery_deadline"), value: "delivery_deadline" },
        { text: this.$t("note"), value: "notes" },
        { text: this.$t("actions"), value: "actions", sortable: false, align: "right" },
      ];
    },
    aggregateMobileHeaders() {
      return [
        { text: this.$t("title"), value: "grocery_name" },
        { text: this.$t("q"), value: "total_amount" },
      ];
    },
  },
  methods: {
    _exportHeaders() {
      if (this.aggregateMode) {
        return [
          { text: this.$t("title"), value: "grocery_name" },
          { text: this.$t("q"), value: "total_amount" },
          { text: this.$t("unit_of_measurment"), value: "grocery_unit" },
        ];
      }
      return this.headers.filter((h) => h.value !== "actions");
    },
    _exportItems() {
      if (this.aggregateMode) {
        return this.aggregatedItems.map((item) => ({
          grocery_name: this.toLatin(item.grocery_name),
          total_amount: item.total_amount,
          grocery_unit: item.grocery_unit,
        }));
      }
      return this.documents.map((doc) => ({
        id: doc.id,
        number: doc.number || "—",
        status: this.$t(this.statusKeyMap[doc.status] || doc.status),
        document_date: this.localeDate(doc.document_date),
        offer_deadline: this.localeDate(doc.offer_deadline),
        restaurant_name: this.toLatin(
          this.restaurants.find((r) => r.id === doc.restaurant_id)?.name || "—"
        ),
        delivery_deadline: this.localeDate(doc.delivery_deadline),
        notes: this.toLatin(doc.notes || ""),
      }));
    },
    print() {
      const { table } = this.printElements;
      const title = this.$t("warehouse.request_quote");
      this.printDoc([table(this._exportHeaders(), this._exportItems())], {
        pageTitle: title,
      });
    },
    async exportExcel() {
      const title = this.$t("warehouse.request_quote");
      await this.downloadExcel({
        fileName: `${title}.xlsx`,
        sheets: [
          {
            name: title.substring(0, 31),
            title,
            headers: this._exportHeaders(),
            items: this._exportItems(),
          },
        ],
      });
    },
  },
};
</script>
